import React, { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import styles from 'src/components/TopNavbar/TopNavbar.styles';
import {
  BoundEnabledPerspective,
  BoundPerspective,
  DisabledBubble,
  RouteBubble,
  Bubble,
  Perspective,
} from 'configuration-schemas/bindings.types';
import { resolvePath } from 'src/cdn';
import {
  bubbleIconStyle,
  bubbleStyle,
  bubbleTitleStyle,
  containerStyle,
  disabledStyle,
  brandLogoCentered,
  nestedBubbleIconStyle,
  nestedBubbleLinkStyle,
  nestedBubbleListIconStyle,
  nestedBubbleListItemStyle,
  nestedBubbleTextStyle,
  nestedBubbleTitleStyle,
  pageStyle,
  userMenuItemStyle,
} from 'src/pages/PerspectiveSelection/PerspectiveSelection.styles';
import { isNil } from 'lodash';
import RestoreSession from 'src/components/RestoreSession/RestoreSession.container';
import {
  getSessionPage,
  getSessionPerspective,
  removeSessionAll,
  setSessionPerspective,
} from 'src/components/RestoreSession/RestoreSession.utils';
import blackTextLogo from 'src/images/S5Logo-Black-Inovation-Centered.png';
import brandLogoPath from 'src/images/S5Logo-White-NoInnovation-Centered.png';
import type { Props } from './PerspectiveSelection.types';
import TopNavIconPopover, { TopNavIconPopoverMenuItem } from 'src/components/TopNavbar/TopNavIconPopover';
import serviceContainer from '../../ServiceContainer';
import { useDispatch } from 'react-redux';
import { setActivePerspective } from '../NavigationShell/NavigationShell.slice';
import { Icon, Collapse, List, ListItem, ListItemIcon, ListItemText } from '@material-ui/core';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import { computePerspectiveRoute } from '../NavigationShell/navigationUtils';
import { DarkModeMenuItem, MatchBrowserThemeMenuItem } from 'src/darkMode/DarkMode';
import { AuthContext } from 'react-oauth2-code-pkce';
import { getPerspectiveLookupKey } from 'src/services/configuration/service.utils';
import type { HasBindings, HasPerspectives } from 'src/utils/Domain/Perspective';

const BrandLogo = resolvePath(brandLogoPath);

const RoutePerspective = (perspective: RouteBubble, index: number): JSX.Element => {
  return (
    <Link
      className={bubbleStyle}
      onClick={removeSessionAll}
      key={index}
      data-id={perspective.title}
      to={perspective.route}
    >
      <div>
        <span className={`${bubbleIconStyle} fa-stack fa-3x`}>
          <i className={perspective.iconSrc} />
        </span>
      </div>
      <label className={bubbleTitleStyle}>{perspective.title}</label>
    </Link>
  );
};

const PerspectiveWithChildren = (bubble: Bubble) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <section className={bubbleStyle} data-id="perspectiveWithChildren" onClick={() => setExpanded(!expanded)}>
      <div>
        <span className={`${bubbleIconStyle} fa-stack fa-3x`}>
          <i className={bubble.iconSrc} />
        </span>
      </div>
      <ListItem className={`${bubbleTitleStyle} ${nestedBubbleListItemStyle}`} dense={true}>
        <span className={nestedBubbleTitleStyle}>
          <label>{bubble.title}</label>
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </span>
      </ListItem>
      <Collapse in={expanded}>
        <List dense={true} disablePadding={true}>
          {bubble.view.map((b) => {
            const enabledPerspective = b.perspective as BoundEnabledPerspective;
            return (
              <NavLink
                className={nestedBubbleLinkStyle}
                to={computePerspectiveRoute(serviceContainer.configService.getBindings(enabledPerspective))}
                key={enabledPerspective.title}
              >
                <ListItem key={enabledPerspective.title} dense={true} disableGutters={true}>
                  <ListItemIcon className={nestedBubbleListIconStyle}>
                    <Icon className={`${nestedBubbleIconStyle} ${enabledPerspective.iconSrc}`} />
                  </ListItemIcon>
                  <ListItemText
                    className={nestedBubbleTextStyle}
                    primary={enabledPerspective.title}
                    disableTypography
                  />
                </ListItem>
              </NavLink>
            );
          })}
        </List>
      </Collapse>
    </section>
  );
};

const DisabledPerspective = (perspective: DisabledBubble, index: number): JSX.Element => {
  return (
    <Link className={bubbleStyle} onClick={removeSessionAll} key={index} to={'/'}>
      <div className={disabledStyle}>
        <span className={`${bubbleIconStyle} fa-stack fa-3x disabled`}>
          <i className={perspective.iconSrc} />
        </span>
      </div>
      <label className={`${disabledStyle} ${bubbleTitleStyle}`}>{perspective.title}</label>
    </Link>
  );
};

const EnabledPerspective = (perspective: BoundEnabledPerspective, index: number): JSX.Element => {
  const dispatch = useDispatch();
  const perspectiveLookupKey = getPerspectiveLookupKey(perspective) as Perspective;
  return (
    <Link
      className={bubbleStyle}
      onClick={() => {
        removeSessionAll();
        dispatch(setActivePerspective(perspective));
        setSessionPerspective(perspectiveLookupKey);
      }}
      key={index}
      data-id={perspectiveLookupKey}
      to={computePerspectiveRoute(serviceContainer.configService.getBindings(perspective))}
    >
      <div>
        <span className={`${bubbleIconStyle} fa-stack fa-3x`}>
          <i className={perspective.iconSrc} />
        </span>
      </div>
      <label className={bubbleTitleStyle}>{perspective.title}</label>
    </Link>
  );
};

const BubbleToChildBubble = (bubble: BoundPerspective, index: number): JSX.Element => {
  switch (bubble.type) {
    case 'Bubble':
      return PerspectiveWithChildren(bubble);
    case 'RouteBubble':
      return RoutePerspective(bubble, index);
    case 'DisabledBubble':
      return DisabledPerspective(bubble, index);
    case 'Perspective':
      return EnabledPerspective(bubble, index);
    default:
      return <div>Invalid bubble config</div>;
  }
};

/* eslint-disable */
const PerspectiveSelection = (config: HasPerspectives & HasBindings) => (props: Props) => {
  const { logOut } = React.useContext(AuthContext);

  const config = serviceContainer.configService;
  const sessionPage = getSessionPage();
  const sessionPerspective = getSessionPerspective();
  const hasLocalStorage = !isNil(sessionPage) && !isNil(sessionPerspective);
  const perspectives = config.getAllPerspectives();
  useEffect(() => {
    props.resetScope();
  }, [props]);

  const isDarkMode = props.darkMode === 'dark';
  const userDropdownMenuItems: TopNavIconPopoverMenuItem[] = [
    {
      children: <DarkModeMenuItem />,
      qaString: 'ToggleTheme',
      onClick: () => undefined,
    },
    {
      children: <MatchBrowserThemeMenuItem />,
      qaString: 'ToggleBrowserTheme',
      onClick: () => undefined,
    },
    {
      children: (
        <span className={userMenuItemStyle}>
          <i className="fa fa-sign-out" />
          <span>Logout</span>
        </span>
      ),
      qaString: 'Logout',
      onClick: logOut,
    },
  ];
  return (
    <div className={pageStyle}>
      <nav className={styles.mainNavBar} data-qa="TopNav">
        <span className="logo-container">
          <Link to="/">
            <img className="logo" src={BrandLogo} alt="" data-qa="TopNavHomeIcon" />
          </Link>
        </span>
        <div className={styles.topNav}>
          <span className="buttons">
            <TopNavIconPopover icon={'fas fa-user user'} iconQaString={'User'} dropdownItems={userDropdownMenuItems} />
          </span>
        </div>
      </nav>
      <img className={brandLogoCentered} src={isDarkMode ? BrandLogo : blackTextLogo} alt="" />
      <div className={containerStyle}>
        {perspectives
          .filter((p) => !p.hidden)
          .map((persp, i) => (
            <React.Fragment key={persp.title}>{BubbleToChildBubble(persp, i)}</React.Fragment>
          ))}
      </div>
      {/* @ts-ignore fix router types later */}
      {hasLocalStorage && <RestoreSession />}
    </div>
  );
};

export default PerspectiveSelection;
